<template>
  <div class="admin-layout">
    <!-- Conteúdo Principal -->
    <main class="admin-content">
      <div class="content-header">
        <div>
          <h1>Painel Administrativo</h1>
          <p class="subtitle">Gerencie as clínicas cadastradas no mapa</p>
        </div>
        <div class="header-actions-container" style="display: flex; gap: 15px;">
          <button class="add-btn-header" @click="openModal()">
             + Nova Clínica
          </button>
          <button class="logout-btn-header" @click="logout" title="Sair">
             <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
             Sair
          </button>
        </div>
      </div>

      <div class="search-bar">
        <input type="text" v-model="search" placeholder="Buscar clínica por nome ou cidade..." />
        
        <select v-model="sortBy" class="sort-select">
          <option value="updatedDesc">Atualizados Recentemente</option>
          <option value="updatedAsc">Atualizados Antigos</option>
          <option value="nameAsc">Nome (A-Z)</option>
          <option value="nameDesc">Nome (Z-A)</option>
          <option value="mapOk">Com Mapa (Coords OK)</option>
          <option value="mapMissing">Sem Mapa (Falta Coords)</option>
          <option value="logoMissing">Sem Logo (Falta Logo)</option>
        </select>
      </div>

      <div class="clinics-grid">
        <div v-if="loading" class="loading">Carregando...</div>
        
        <div v-else-if="filteredClinics.length === 0" class="empty">
          Nenhuma clínica encontrada.
        </div>

        <div v-else v-for="clinic in filteredClinics" :key="clinic.id" class="clinic-card-admin">
          <div class="card-header">
             <img :src="clinic.logo || placeholder" class="clinic-logo" @error="handleImageError" />
             <div class="clinic-status" :class="{ 'has-coords': clinic.lat && clinic.lng }">
               {{ (clinic.lat && clinic.lng) ? '📍 Localizada' : '⚠️ Sem Mapa' }}
             </div>
          </div>
          <h3>{{ clinic.name }}</h3>
          <p class="address">{{ clinic.address }}</p>
          
          <div class="actions">
            <button class="edit-btn" @click="openModal(clinic)">Editar</button>
            <button class="delete-btn" @click="confirmDelete(clinic)">Excluir</button>
          </div>
        </div>
      </div>
    </main>

    <!-- Toast Notification -->
    <div v-if="toastMessage" class="toast-notification" :class="toastType">
      {{ toastMessage }}
    </div>

    <!-- Modal de Edição/Criação -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <h2>{{ editingClinic ? 'Editar Clínica' : 'Nova Clínica' }}</h2>
          <button class="close-modal-btn" @click="closeModal">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
        
        <div class="modal-body">
        <form @submit.prevent="saveClinic" class="clinic-form">
          
          <!-- Seção de Imagem -->
          <div class="image-upload-section">
            <div class="image-preview" @click="triggerFileInput" :class="{ 'loading-upload': uploadLoading }">
              <img v-if="imagePreview" :src="imagePreview" alt="Preview" class="preview-img" />
              <div v-else class="upload-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                <span>Upload Logo</span>
              </div>
              <div v-if="uploadLoading" class="upload-overlay">
                <div class="spinner-small"></div>
              </div>
            </div>
            <input type="file" ref="fileInput" @change="handleFileChange" accept="image/png, image/jpeg, image/webp" style="display: none;" />
            <div class="upload-info">
              <p class="upload-label">Logotipo da Clínica</p>
              <p class="upload-help">Clique na imagem para alterar. PNG ou JPG.</p>
            </div>
          </div>

          <div class="form-section-title">Informações Básicas</div>

          <div class="form-row">
            <div class="form-group full">
              <label>Nome da Clínica *</label>
              <input v-model="form.name" required placeholder="Ex: Clínica Saúde Total" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full">
               <label>Endereço Completo</label>
               <div class="address-input-group">
                 <input v-model="form.address" required placeholder="Rua, Número, Cidade, País" />
                 <button type="button" class="geo-btn" @click="findCoordinates" :disabled="geoLoading">
                   {{ geoLoading ? '...' : '📍' }}
                 </button>
               </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full">
              <label>Coordenadas (Lat, Lng) *</label>
              <input v-model="form.coordsInput" required placeholder="-23.5505, -46.6333" @input="parseCoords" />
            </div>
          </div>

          <div class="form-section-title">Contato & Social</div>

          <div class="form-row">
            <div class="form-group">
              <label>Whatsapp</label>
              <input v-model="form.whatsapp" placeholder="5511999999999" />
            </div>
            <div class="form-group">
              <label>Telefone</label>
              <input v-model="form.contact" placeholder="+55 (11) 3333-4444" />
            </div>
          </div>
          
          <div class="form-row">
             <div class="form-group full">
              <label>Instagram</label>
              <input v-model="form.instagram" placeholder="https://instagram.com/..." />
            </div>
          </div>

          <div class="form-row">
             <div class="form-group full">
              <label>Site</label>
              <input v-model="form.website" placeholder="https://minhaclinica.com.br" />
            </div>
          </div>

          <div class="form-section-title">Detalhes Google</div>

          <div class="form-row">
             <div class="form-group full">
              <label>Link Google Meu Negócio</label>
              <input v-model="form.gmbLink" placeholder="https://g.page/..." />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Nota (0-5)</label>
              <input v-model="form.rating" type="number" step="0.1" min="0" max="5" placeholder="4.9" />
            </div>
            <div class="form-group">
              <label>Avaliações</label>
              <input v-model="form.reviewsCount" type="number" placeholder="120" />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="cancel-btn" @click="closeModal">Cancelar</button>
            <button type="submit" class="save-btn" :disabled="saving || uploadLoading">
              {{ saving ? 'Salvando...' : 'Salvar Alterações' }}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { auth, db, storage } from '@/firebaseConfig';
import { signOut } from 'firebase/auth';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
const placeholder = 'https://firebasestorage.googleapis.com/v0/b/system-oligocheck.appspot.com/o/clinics%2F1757882609228_SOCHECK.jpeg?alt=media&token=6b02c18c-cc05-4d35-a4c3-f3074e6b4c3f';

const router = useRouter();
const clinics = ref([]);
const loading = ref(true);
const search = ref('');
const sortBy = ref('updatedDesc');
const showModal = ref(false);
const editingClinic = ref(null);
const saving = ref(false);
const geoLoading = ref(false);
const uploadLoading = ref(false);
const fileInput = ref(null);
const imagePreview = ref(null);
const toastMessage = ref('');
const toastType = ref('success');

const showToast = (msg, type = 'success') => {
  toastMessage.value = msg;
  toastType.value = type;
  setTimeout(() => {
    toastMessage.value = '';
  }, 3000);
};

const form = ref({
  name: '',
  address: '',
  lat: null,
  lng: null,
  coordsInput: '',
  whatsapp: '',
  contact: '',
  instagram: '',
  logo: ''
});

onMounted(async () => {
  console.log('AdminDashboard montado');
  await fetchClinics();
});

const fetchClinics = async () => {
  loading.value = true;
  try {
    const user = auth.currentUser;
    if (!user) {
      clinics.value = [];
      return;
    }

    const q = query(collection(db, "clinics"), where("ownerId", "==", user.uid));
    const querySnapshot = await getDocs(q);
    clinics.value = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (e) {
    console.error("Erro ao buscar clínicas", e);
  } finally {
    loading.value = false;
  }
};

const filteredClinics = computed(() => {
  let result = clinics.value;

  // 1. Filtro de Busca
  if (search.value) {
    const s = search.value.toLowerCase();
    result = result.filter(c => 
      (c.name && c.name.toLowerCase().includes(s)) || 
      (c.address && c.address.toLowerCase().includes(s))
    );
  }

  // 2. Ordenação
  result = [...result].sort((a, b) => {
    switch (sortBy.value) {
      case 'nameAsc':
        return a.name.localeCompare(b.name);
      case 'nameDesc':
        return b.name.localeCompare(a.name);
      case 'updatedDesc':
        return (b.updatedAt || 0) - (a.updatedAt || 0);
      case 'updatedAsc':
        return (a.updatedAt || 0) - (b.updatedAt || 0);
      case 'mapOk':
        return (b.lat && b.lng ? 1 : 0) - (a.lat && a.lng ? 1 : 0); // Com coords primeiro
      case 'mapMissing':
        return (a.lat && a.lng ? 1 : 0) - (b.lat && b.lng ? 1 : 0); // Sem coords primeiro
      case 'logoMissing':
        return (a.logo ? 1 : 0) - (b.logo ? 1 : 0); // Sem logo primeiro
      default:
        return 0;
    }
  });

  return result;
});

// Computed properties for stats
const clinicsWithoutMap = computed(() => {
  return clinics.value.filter(c => !c.lat || !c.lng);
});

const clinicsIncomplete = computed(() => {
  return clinics.value.filter(c => !c.contact && !c.whatsapp);
});

const logout = async () => {
  await signOut(auth);
  router.push('/admin/login');
};

const openModal = (clinic = null) => {
  // Bloqueia o scroll da página
  document.body.style.overflow = 'hidden';
  
  imagePreview.value = null; // Reset preview
  if (clinic) {
    editingClinic.value = clinic;
    form.value = { ...clinic };
    imagePreview.value = clinic.logo || null; // Show current logo if exists
    // Preenche o campo único se já tiver coords
    if (clinic.lat && clinic.lng) {
      form.value.coordsInput = `${clinic.lat}, ${clinic.lng}`;
    } else {
      form.value.coordsInput = '';
    }
  } else {
    editingClinic.value = null;
    form.value = {
      name: '',
      address: '',
      lat: null,
      lng: null,
      coordsInput: '',
      whatsapp: '',
      contact: '',
      instagram: '',
      website: '',
      gmbLink: '',
      rating: '',
      reviewsCount: '',
      logo: ''
    };
  }
  showModal.value = true;
};

const closeModal = () => {
  // Libera o scroll da página
  document.body.style.overflow = '';
  showModal.value = false;
};

const handleImageError = (e) => {
  e.target.src = placeholder;
};

const triggerFileInput = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validar tipo
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    showToast('Apenas arquivos JPG, PNG ou WEBP são permitidos.', 'error');
    return;
  }

  // Validar tamanho (ex: 2MB)
  if (file.size > 2 * 1024 * 1024) {
    showToast('A imagem deve ter no máximo 2MB.', 'error');
    return;
  }

  // Preview imediato
  const reader = new FileReader();
  reader.onload = (e) => {
    imagePreview.value = e.target.result;
  };
  reader.readAsDataURL(file);

  // Upload automático
  uploadLoading.value = true;
  try {
    // Sanitizar nome do arquivo (remove espaços e caracteres especiais)
    const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const fileName = `clinics/${Date.now()}_${safeFileName}`;
    const fileReference = storageRef(storage, fileName);
    
    const snapshot = await uploadBytes(fileReference, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    form.value.logo = downloadURL;
    showToast('Imagem enviada com sucesso!');
  } catch (error) {
    console.error('Erro upload:', error);
    showToast('Falha ao enviar imagem.', 'error');
    imagePreview.value = form.value.logo; // Reverte preview se falhar
  } finally {
    uploadLoading.value = false;
  }
};

const findCoordinates = async () => {
  if (!form.value.address) {
    alert('Digite um endereço primeiro');
    return;
  }
  geoLoading.value = true;
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.value.address)}&limit=1`);
    const data = await response.json();
    if (data && data.length > 0) {
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);
      form.value.lat = lat;
      form.value.lng = lng;
      form.value.coordsInput = `${lat}, ${lng}`;
    } else {
      alert('Endereço não encontrado. Tente ser mais específico (Cidade, País).');
    }
  } catch (e) {
    alert('Erro ao buscar coordenadas.');
  } finally {
    geoLoading.value = false;
  }
};

const parseCoords = () => {
  // Tenta extrair lat/lng da string colada (ex: "-23.5, -46.6")
  if (!form.value.coordsInput) {
    form.value.lat = null;
    form.value.lng = null;
    return;
  }
  const parts = form.value.coordsInput.split(',');
  if (parts.length === 2) {
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    if (!isNaN(lat) && !isNaN(lng)) {
      form.value.lat = lat;
      form.value.lng = lng;
    }
  }
};

const saveClinic = async () => {
  saving.value = true;
  try {
    // Validação de logo obrigatória
    if (!form.value.logo) {
      showToast('Por favor, faça o upload da logo da clínica antes de salvar.', 'error');
      saving.value = false;
      return;
    }

    // Garante que o parser rodou
    parseCoords();

    // Limpa dados undefined e remove coordsInput auxiliar
    const dataToSave = JSON.parse(JSON.stringify(form.value));
    delete dataToSave.coordsInput;
    
    // Adiciona Timestamp para ordenação
    dataToSave.updatedAt = Date.now();

    if (editingClinic.value) {
      await updateDoc(doc(db, "clinics", editingClinic.value.id), dataToSave);
      showToast('Clínica atualizada com sucesso!');
    } else {
      dataToSave.createdAt = Date.now(); // Criação apenas para novos
      dataToSave.ownerId = auth.currentUser.uid; // Vincula ao admin atual
      await addDoc(collection(db, "clinics"), dataToSave);
      showToast('Clínica criada com sucesso!');
    }
    closeModal();
    fetchClinics();
  } catch (e) {
    console.error(e);
    showToast('Erro ao salvar.', 'error');
  } finally {
    saving.value = false;
  }
};

const confirmDelete = async (clinic) => {
  if (confirm(`Tem certeza que deseja excluir ${clinic.name}?`)) {
    try {
      await deleteDoc(doc(db, "clinics", clinic.id));
      showToast('Clínica excluída.');
      fetchClinics();
    } catch (e) {
      showToast('Erro ao excluir.', 'error');
    }
  }
};
</script>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  background-color: #f5f7f9;
}

.admin-content {
  flex: 1;
  padding: 40px;
  max-width: 100%;
}

.logout-btn-header {
  background-color: #fef2f2;
  color: #ef4444;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--bold);
  transition: all 0.2s;
  font-size: 14px;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
}

.logout-btn-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.2);
  background-color: #fee2e2;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 30px;
}

h1 {
  font-family: var(--bold);
  font-size: 28px;
  color: var(--cor-escuro-1);
  margin: 0 0 5px 0;
}

.subtitle {
  color: #64748b;
  margin: 0;
  font-size: 14px;
  font-family: var(--regular);
}

.add-btn-header {
  background-color: #2c3e50;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--bold);
  transition: all 0.2s;
  font-size: 14px;
  box-shadow: 0 4px 15px rgba(44, 62, 80, 0.2);
}

.add-btn-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(44, 62, 80, 0.3);
}

.search-bar {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
}

.search-bar input {
  flex: 2;
  padding: 15px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  font-family: var(--regular);
  background: white;
  outline: none;
  transition: border-color 0.2s;
}

.search-bar input:focus {
  border-color: #2c3e50;
}

.sort-select {
  flex: 1;
  padding: 15px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-family: var(--regular);
  background-color: white;
  cursor: pointer;
  outline: none;
}

.clinics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
}

.clinic-card-admin {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
}

.clinic-card-admin:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0,0,0,0.06);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.clinic-logo {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #f1f5f9;
}

.clinic-status {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  background: #fff7ed;
  color: #b45309;
  font-family: var(--bold);
}

.clinic-status.has-coords {
  background: #ecfdf5;
  color: #047857;
}

h3 {
  font-family: var(--bold);
  font-size: 18px;
  margin: 0 0 8px 0;
  color: var(--cor-escuro-1);
}

.address {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
  flex: 1;
  font-family: var(--regular);
  line-height: 1.5;
}

.actions {
  display: flex;
  gap: 10px;
  margin-top: auto;
}

.edit-btn, .delete-btn {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-family: var(--bold);
  transition: background 0.2s;
}

.edit-btn {
  background-color: #f1f5f9;
  color: #334155;
}

.edit-btn:hover {
  background-color: #e2e8f0;
}

.delete-btn {
  background-color: #fef2f2;
  color: #ef4444;
}

.delete-btn:hover {
  background-color: #fee2e2;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.2); /* Mais leve */
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-box {
  background: rgba(255, 255, 255, 0.95);
  padding: 0;
  border-radius: 32px;
  width: 90%;
  max-width: 550px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0,0,0,0.15);
  border: 1px solid white;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  padding: 25px 30px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  background: #fff;
  z-index: 10;
  flex-shrink: 0;
}

.modal-box h2 {
  font-size: 24px;
  margin: 0;
  color: var(--cor-escuro-1);
  font-family: 'Inter-Bold', 'Inter Bold', sans-serif !important;
}

.close-modal-btn {
  background: #f1f5f9;
  border: none;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-modal-btn:hover {
  color: #ef4444;
  background: #fee2e2;
  transform: rotate(90deg);
}

.modal-body {
  padding: 10px 30px 30px 30px;
  overflow-y: auto;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

/* Custom Scrollbar for Modal Body */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: #e2e8f0;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

/* Remove old modal-box scrollbar styles */
.modal-box::-webkit-scrollbar {
  display: none;
}

.image-upload-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 20px;
  border: 1px solid #f1f5f9;
}

.image-preview {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: white;
  border: 2px dashed #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: all 0.2s;
}

.image-preview:hover {
  border-color: #2c3e50;
  transform: scale(1.02);
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #94a3b8;
  font-size: 9px;
  text-align: center;
}

.upload-placeholder svg {
  width: 20px;
  height: 20px;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #2c3e50;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.upload-info {
  flex: 1;
}

.upload-label {
  font-family: var(--bold);
  color: var(--cor-escuro-1);
  font-size: 14px;
  margin: 0 0 4px 0;
}

.upload-help {
  color: #64748b;
  font-size: 12px;
  margin: 0;
  font-family: 'Inter-Regular', 'Inter Regular', sans-serif !important;
}

.form-section-title {
  font-size: 12px;
  text-transform: uppercase;
  color: #94a3b8;
  font-family: var(--bold);
  letter-spacing: 0.5px;
  margin: 20px 0 10px 4px;
  padding-bottom: 5px;
  border-bottom: 1px solid #f1f5f9;
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-group {
  flex: 1;
}

.form-group.full {
  flex: 100%;
}

label {
  display: block;
  font-size: 12px;
  font-family: var(--bold);
  margin-bottom: 6px;
  color: #475569;
  margin-left: 4px;
}

input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-family: var(--regular);
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  background: #f8fafc;
  color: var(--cor-escuro-1);
}

input:focus {
  border-color: #2c3e50;
  background: white;
  box-shadow: 0 0 0 3px rgba(44, 62, 80, 0.1);
}

.address-input-group {
  display: flex;
  gap: 8px;
}

.geo-btn {
  width: 46px;
  background: #f8fafc;
  color: #2c3e50;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.2s;
}

.geo-btn:hover {
  background: #e2e8f0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
}

.cancel-btn {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  font-family: var(--bold);
  color: #64748b;
  font-size: 14px;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #f8fafc;
  color: #475569;
}

.save-btn {
  background: #2c3e50;
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 12px;
  cursor: pointer;
  font-family: var(--bold);
  font-size: 14px;
  box-shadow: 0 4px 15px rgba(44, 62, 80, 0.25);
  transition: all 0.2s;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(44, 62, 80, 0.35);
}

.save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Removed old styles */


/* Toast Notification */
.toast-notification {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: #333;
  color: white;
  padding: 15px 25px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  z-index: 3000;
  font-family: var(--bold);
  animation: slideIn 0.3s ease-out;
}

.toast-notification.success {
  background: #10b981;
}

.toast-notification.error {
  background: #ef4444;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>